import { Trans, useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { IconEquals } from "../../../../common/icons/IconEquals/IconEquals";
import { IconMultiply } from "../../../../common/icons/IconMultiply/IconMultiply";
import "./EarningsCalculatorCalculationTile.scss";
import LeafTileDivider from "../../../../common/leaf/LeafTileDivider/LeafTileDivider";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ActualFormatted } from "../../../../services/EarningsCalculator/earningCalculatorAPI.types";

export type IncomeData = {
  IsQualified?: boolean;
  Basis: ActualFormatted;
  Multiplier: ActualFormatted;
  Bonus: ActualFormatted;
};

type Props = {
  IsLoading: boolean;
  IsQualified?: boolean;
  IncomeData: IncomeData[];
  Content: {
    Basis: string;
    BasisWithCount?: boolean;
    Multiplier: string;
    Bonus: string;
    NotQualified: string;
  };
};

const EarningsCalculatorCalculationTile = (props: Props) => {
  const { IsLoading, IsQualified = true, IncomeData, Content } = props;
  const { Basis, Multiplier, Bonus, NotQualified, BasisWithCount = false } = Content;

  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(["earningsCalculator"]);

  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const el = containerRef.current;
      if (el) {
        setHasOverflow(el.scrollHeight > el.clientHeight);
      }
    };

    checkOverflow();
  }, []);

  const renderBonusTile = () => {
    const defaultData = [
      {
        Basis: {
          Formatted: "",
        },
        Multiplier: {
          Formatted: "",
        },
        Bonus: {
          Formatted: "",
        },
      },
    ];

    const renderIncomeData = IsLoading && IncomeData.length === 0 ? defaultData : IncomeData;
    return renderIncomeData.map((bonusData, index) => (
      <div key={index}>
        <div className="earnings-calculator-calculation-tile__content">
          <div className="earnings-calculator-calculation-tile__cell">
            <div className="earnings-calculator-calculation-tile__symbol--mobile"></div>
            <div className="earnings-calculator-calculation-tile__cell-content">
              <div className="earnings-calculator-calculation-tile__label">
                {BasisWithCount ? <Trans defaults={Basis} values={{ count: index + 1 }} /> : Basis}
              </div>
              <div className="earnings-calculator-calculation-tile__amount">
                <LeafRectangleLoader isLoading={IsLoading} width={"100%"} height={"100%"}>
                  {bonusData.Basis.Formatted}
                </LeafRectangleLoader>
              </div>
            </div>
          </div>

          <div className="earnings-calculator-calculation-tile__symbol">
            <IconMultiply />
          </div>

          <div className="earnings-calculator-calculation-tile__cell">
            <div className="earnings-calculator-calculation-tile__symbol--mobile">
              <IconMultiply />
            </div>
            <div className="earnings-calculator-calculation-tile__cell-content">
              <div className="earnings-calculator-calculation-tile__label">{Multiplier}</div>
              <div className="earnings-calculator-calculation-tile__amount">
                <LeafRectangleLoader isLoading={IsLoading} width={"100%"} height={"100%"}>
                  {bonusData.Multiplier.Formatted}
                </LeafRectangleLoader>
              </div>
            </div>
          </div>

          <div className="earnings-calculator-calculation-tile__cell earnings-calculator-calculation-tile__divider--mobile">
            <LeafTileDivider />
          </div>

          <div className="earnings-calculator-calculation-tile__symbol">
            <IconEquals />
          </div>

          <div className="earnings-calculator-calculation-tile__cell">
            <div className="earnings-calculator-calculation-tile__symbol--mobile">
              <IconEquals />
            </div>
            <div className="earnings-calculator-calculation-tile__cell-content">
              <div className="earnings-calculator-calculation-tile__label">{Bonus}</div>
              <div className="earnings-calculator-calculation-tile__amount">
                <LeafRectangleLoader isLoading={IsLoading} width={"100%"} height={"100%"}>
                  {IsQualified ? bonusData.Bonus.Formatted : `${t(NotQualified, { defaultValue: NotQualified })}`}
                </LeafRectangleLoader>
              </div>
            </div>
          </div>
        </div>
        {index + 1 < IncomeData.length ? (
          <div
            className={
              "earnings-calculator-calculation-tile__cell earnings-calculator-calculation-tile__bottom-divider"
            }
          >
            <LeafTileDivider />{" "}
          </div>
        ) : (
          <></>
        )}
      </div>
    ));
  };

  return (
    <div ref={containerRef} className={`earnings-calculator-calculation-tile ${hasOverflow ? "" : "scrollbar-hidden"}`}>
      {renderBonusTile()}
    </div>
  );
};

export default EarningsCalculatorCalculationTile;
